import { useRef, type Ref, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import { useGetFriendGroupSuspense, useUpdateFriendGroup } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardSubtitleHeader, CardTextContent } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Input } from "@/components/Input";

export function EditFriendGroupModal({
  ref,
  friendGroupId,
  onClose,
}: {
  ref: Ref<HTMLDialogElement>;
  friendGroupId: number;
  onClose?: () => void;
}) {
  const friendGroup = useGetFriendGroupSuspense(friendGroupId, {
    query: { select: (data) => data.data.friend_group },
  });
  const { t } = useTranslation();
  const cancel = useRef<HTMLButtonElement>(null);

  const updateFriendGroup = useUpdateFriendGroup({
    mutation: {
      onSuccess: () => onClose?.(),
    },
  });

  function submit(event: SubmitEvent<HTMLFormElement>) {
    if (event.nativeEvent.submitter === cancel.current) {
      event.currentTarget.reset();
    } else {
      const form = new FormData(event.currentTarget);
      const name = form.get("name")! as string;
      const description = form.get("description")! as string;
      updateFriendGroup.mutate({ friendGroupId, data: { name, description, sort: friendGroup.data.sort } });
    }
  }

  return (
    <Dialog ref={ref} className="flex w-lg flex-col">
      <CardSubtitleHeader>{t("edit-friend-group")}</CardSubtitleHeader>
      <CardTextContent>
        <Form error={updateFriendGroup.error} className="flex grow flex-col gap-4" onSubmit={submit}>
          <FormField title={t("friend-group-name-title")}>
            <Input
              required
              type="text"
              defaultValue={friendGroup.data.name}
              name="name"
              placeholder={t("friend-group-name-placeholder")}
            />
          </FormField>
          <FormField title={t("friend-group-description-title")}>
            <Input
              type="text"
              defaultValue={friendGroup.data.description ?? ""}
              name="description"
              placeholder={t("friend-group-description-placeholder")}
            />
          </FormField>
          <ActionBox>
            <Button type="button" name="action" attention="danger" onClick={onClose} ref={cancel}>
              {t("cancel")}
            </Button>
            <Button type="submit" attention="submit">
              {t("save")}
            </Button>
          </ActionBox>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
